// --- React
import React from 'react';
// --- UI Components
import Select from '@dtci/spellbook/Select';
import { Pagination } from '../Pagination';
// --- Utils
import { DEFAULT_PAGE_SIZE_OPTIONS } from 'datastage/utils/paginationPreferences.js';
// --- Styles
import './styles.scss';

/**
 * Shared pagination bar with page navigation, page size selector, and record count.
 *
 * Provides a consistent pagination UI across all paginated pages, including:
 * - Page navigation controls (← →)
 * - Page size dropdown selector ("Show [10/20/50/100] per page")
 * - Record count display ("1-20 of 100 records")
 *
 * @component
 * @param {Object} props
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.currentPage - Current zero-based page index
 * @param {number} props.totalItems - Total record count
 * @param {number} props.itemsPerPage - Current items per page
 * @param {Function} props.onPageChange - Called with page number when page changes
 * @param {Function} props.onPageSizeChange - Called with new page size when dropdown changes
 * @param {Array} [props.pageSizeOptions] - Custom page size options (default: [10, 20, 50, 100])
 *
 * @example
 * <PaginationBar
 *   totalPages={5}
 *   currentPage={0}
 *   totalItems={100}
 *   itemsPerPage={20}
 *   onPageChange={handlePageChange}
 *   onPageSizeChange={handlePageSizeChange}
 * />
 */
export function PaginationBar({
    totalPages,
    currentPage,
    totalItems,
    itemsPerPage,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}) {
    // Calculate visible range for current page
    const rangeStart = totalItems > 0 ? currentPage * itemsPerPage + 1 : 0;
    const rangeEnd = Math.min(rangeStart + itemsPerPage - 1, totalItems);

    // Convert page size options to Select format
    const selectOptions = pageSizeOptions.map((size) => ({
        label: String(size),
        value: size,
    }));

    return (
        <div className="pagination-bar">
            {/* Left section - currently empty, can be used for bulk actions */}
            <div className="pagination-bar-left" />

            {/* Center section - page navigation */}
            <div className="pagination-bar-center">
                <Pagination
                    pageCount={totalPages}
                    onPageChange={(e) => onPageChange(e.selected)}
                    previousLabel="←"
                    nextLabel="→"
                    forcePage={currentPage}
                />
            </div>

            {/* Right section - page size selector and record count */}
            <div className="pagination-bar-right">
                <div className="pagination-bar-page-size">
                    <span>Show</span>
                    <Select
                        className="pagination-bar-page-size-select"
                        options={selectOptions}
                        value={selectOptions.find((o) => o.value === itemsPerPage)}
                        onChange={(opt) => onPageSizeChange(opt.value)}
                        isSearchable={false}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                    />
                    <span>per page</span>
                </div>
                <span className="pagination-bar-record-count">
                    {totalItems > 0
                        ? `${rangeStart}-${rangeEnd} of ${totalItems} records`
                        : 'No records'}
                </span>
            </div>
        </div>
    );
}
